import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  Tag, 
  Button, 
  Tooltip, 
  Typography, 
  Space, 
  Collapse, 
  List,
  Badge,
  Divider
} from 'antd';
import {
  PlusOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { getHealthScoreColor } from '../utils/healthColorUtils';

const { Text, Title, Paragraph } = Typography;
const { Panel } = Collapse;

const LifestyleOrderCard = ({ 
  card, 
  isSelected = false, 
  onSelect, 
  onDeselect,
  patientData = null,
  showDetailedView = false,
  onToggleDetailedView
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!card) return null;

  // Check if card is relevant based on patient data
  const getRelevanceScore = () => {
    if (!patientData || !card.targetParameters) {
      console.log(`[LifestyleOrderCard] No patient data or target parameters for card: ${card.title}`);
      return 50;
    }

    let relevantParams = 0;
    let totalParams = card.targetParameters.length;
    // Filter out undefined/null values from the parameter keys
    const availableParameters = (patientData.tableData?.map(row => row.parameterKey) || [])
      .filter(param => param != null && typeof param === 'string');

    console.log(`[LifestyleOrderCard] Checking relevance for card: ${card.title}`);
    console.log(`[LifestyleOrderCard] Target parameters:`, card.targetParameters);
    console.log(`[LifestyleOrderCard] Available lifestyle parameters:`,
      availableParameters.filter(param => param && param.startsWith('lifestyle_')));

    card.targetParameters.forEach(param => {
      // Check if patient has data for this parameter
      const hasData = patientData.tableData?.some(row => row.parameterKey === param);
      if (hasData) {
        relevantParams++;
        console.log(`[LifestyleOrderCard] Found data for parameter: ${param}`);
      } else {
        console.log(`[LifestyleOrderCard] No data for parameter: ${param}`);
      }
    });

    // Enhanced relevancy calculation
    let baseRelevance = totalParams > 0 ? (relevantParams / totalParams) * 100 : 50;

    // If no exact matches, check for related lifestyle parameters in the same category
    if (relevantParams === 0) {
      const cardCategory = card.category.toLowerCase().replace(/\s+/g, '_');
      const relatedParams = availableParameters.filter(param =>
        param && param.startsWith('lifestyle_') && param.includes(cardCategory)
      );

      if (relatedParams.length > 0) {
        baseRelevance = Math.min(75, 25 + (relatedParams.length * 10)); // Cap at 75% for related matches
        console.log(`[LifestyleOrderCard] Found ${relatedParams.length} related parameters, setting relevance to ${baseRelevance}%`);
      }
    }

    console.log(`[LifestyleOrderCard] Final relevance score for ${card.title}: ${Math.round(baseRelevance)}%`);
    return baseRelevance;
  };

  const relevanceScore = getRelevanceScore();
  const relevanceColor = getHealthScoreColor(relevanceScore);

  // Handle card selection
  const handleCardAction = () => {
    if (isSelected) {
      onDeselect?.(card);
    } else {
      onSelect?.(card);
    }
  };

  // Render evidence level badge
  const renderEvidenceLevel = () => (
    <Tooltip title={card.evidenceLevel.description}>
      <Tag 
        color={card.evidenceLevel.color}
        style={{ fontWeight: 'bold' }}
      >
        Level {card.evidenceLevel.level}
      </Tag>
    </Tooltip>
  );

  // Render difficulty badge
  const renderDifficultyLevel = () => (
    <Tooltip title={`${card.difficulty.level} difficulty level`}>
      <Tag color={card.difficulty.color}>
        {card.difficulty.icon} {card.difficulty.level}
      </Tag>
    </Tooltip>
  );

  // Render time commitment badge
  const renderTimeCommitment = () => (
    <Tooltip title="Daily time commitment">
      <Tag color={card.timeCommitment.color} icon={<ClockCircleOutlined />}>
        {card.timeCommitment.label}
      </Tag>
    </Tooltip>
  );

  // Render patient relevance indicator
  const renderRelevanceIndicator = () => (
    <Tooltip title={`${Math.round(relevanceScore)}% relevant based on patient data`}>
      <Badge 
        count={`${Math.round(relevanceScore)}%`}
        style={{ 
          backgroundColor: relevanceColor,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    </Tooltip>
  );

  // Render card header
  const renderCardHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
          {card.title}
        </Title>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {card.category} • {card.subcategory}
        </Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        {patientData && renderRelevanceIndicator()}
        <Button
          type={isSelected ? "primary" : "default"}
          size="small"
          icon={isSelected ? <MinusOutlined /> : <PlusOutlined />}
          onClick={handleCardAction}
          style={{
            backgroundColor: isSelected ? '#1890ff' : undefined,
            borderColor: isSelected ? '#1890ff' : undefined
          }}
        >
          {isSelected ? 'Remove' : 'Add'}
        </Button>
      </div>
    </div>
  );

  // Render card badges
  const renderBadges = () => (
    <Space wrap style={{ marginTop: 8, marginBottom: 8 }}>
      {renderEvidenceLevel()}
      {renderDifficultyLevel()}
      {renderTimeCommitment()}
    </Space>
  );

  // Render basic card content
  const renderBasicContent = () => (
    <div>
      <Paragraph style={{ margin: '8px 0', fontSize: '13px' }}>
        {card.description}
      </Paragraph>
      
      <div style={{ marginTop: 12 }}>
        <Text strong style={{ fontSize: '12px' }}>Duration: </Text>
        <Text style={{ fontSize: '12px' }}>{card.duration}</Text>
      </div>
      
      {card.smartGoals && card.smartGoals.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ fontSize: '12px' }}>Key Goals:</Text>
          <List
            size="small"
            dataSource={card.smartGoals.slice(0, 2)}
            renderItem={(goal) => (
              <List.Item style={{ padding: '2px 0', fontSize: '12px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                {goal}
              </List.Item>
            )}
          />
          {card.smartGoals.length > 2 && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              +{card.smartGoals.length - 2} more goals...
            </Text>
          )}
        </div>
      )}
    </div>
  );

  // Render detailed content in collapse
  const renderDetailedContent = () => (
    <Collapse 
      ghost 
      size="small"
      activeKey={isExpanded ? ['details'] : []}
      onChange={(keys) => setIsExpanded(keys.includes('details'))}
    >
      <Panel 
        header={
          <Text style={{ fontSize: '12px' }}>
            <EyeOutlined /> View Clinical Details
          </Text>
        } 
        key="details"
      >
        <div style={{ fontSize: '12px' }}>
          {/* Clinical Rationale */}
          <div style={{ marginBottom: 12 }}>
            <Text strong>Clinical Rationale:</Text>
            <Paragraph style={{ margin: '4px 0', fontSize: '12px' }}>
              {card.clinicalRationale}
            </Paragraph>
          </div>

          {/* All SMART Goals */}
          {card.smartGoals && (
            <div style={{ marginBottom: 12 }}>
              <Text strong>Complete SMART Goals:</Text>
              <List
                size="small"
                dataSource={card.smartGoals}
                renderItem={(goal) => (
                  <List.Item style={{ padding: '2px 0', fontSize: '12px' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                    {goal}
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Contraindications */}
          {card.contraindications && card.contraindications.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: '#ff4d4f' }}>
                <WarningOutlined /> Contraindications:
              </Text>
              <List
                size="small"
                dataSource={card.contraindications}
                renderItem={(item) => (
                  <List.Item style={{ padding: '2px 0', fontSize: '12px' }}>
                    • {item}
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Monitoring */}
          {card.monitoring && card.monitoring.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong>Monitoring Parameters:</Text>
              <List
                size="small"
                dataSource={card.monitoring}
                renderItem={(item) => (
                  <List.Item style={{ padding: '2px 0', fontSize: '12px' }}>
                    • {item}
                  </List.Item>
                )}
              />
            </div>
          )}

          {/* Expected Outcomes */}
          <div style={{ marginBottom: 12 }}>
            <Text strong>Expected Outcomes:</Text>
            <Paragraph style={{ margin: '4px 0', fontSize: '12px' }}>
              {card.expectedOutcomes}
            </Paragraph>
          </div>

          {/* Citations */}
          {card.citations && card.citations.length > 0 && (
            <div>
              <Text strong>Evidence Citations:</Text>
              <List
                size="small"
                dataSource={card.citations}
                renderItem={(citation) => (
                  <List.Item style={{ padding: '2px 0', fontSize: '11px' }}>
                    <Text type="secondary">{citation}</Text>
                  </List.Item>
                )}
              />
            </div>
          )}
        </div>
      </Panel>
    </Collapse>
  );

  return (
    <Card
      size="small"
      hoverable
      style={{
        height: '100%',
        border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
        borderRadius: '8px',
        backgroundColor: isSelected ? '#f6ffed' : 'white',
        transition: 'all 0.3s ease'
      }}
      bodyStyle={{ padding: '12px' }}
    >
      {renderCardHeader()}
      {renderBadges()}
      <Divider style={{ margin: '8px 0' }} />
      {renderBasicContent()}
      {renderDetailedContent()}
    </Card>
  );
};

LifestyleOrderCard.propTypes = {
  card: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  onDeselect: PropTypes.func,
  patientData: PropTypes.object,
  showDetailedView: PropTypes.bool,
  onToggleDetailedView: PropTypes.func
};

export default LifestyleOrderCard;
